import { useEffect, useRef, useState, useCallback } from 'react'

declare global {
  interface Window {
    myCallbackFunction?: (token: string) => void
    xxTrustedFormCertUrl?: any
    xxTrustedFormToken?: string
    xxTrustedFormPingUrl?: any
  }
}

function isCertUrl(val: unknown): val is string {
  return (
    typeof val === 'string' &&
    val.indexOf('https://cert.trustedform.com/') === 0
  )
}

function scanForTrustedFormUrl(): string {
  // 1. Check all inputs with the TrustedForm field name
  const inputs = document.querySelectorAll('input[name="xxTrustedFormCertUrl"]')
  for (let i = 0; i < inputs.length; i++) {
    const el = inputs[i] as HTMLInputElement
    if (isCertUrl(el.value)) return el.value
  }

  // 2. Check window.xxTrustedFormCertUrl (could be string, element, or other)
  const winCert = window.xxTrustedFormCertUrl
  if (winCert) {
    if (isCertUrl(winCert)) return winCert
    if (winCert instanceof HTMLInputElement && isCertUrl(winCert.value))
      return winCert.value
    // Some versions store it as .value on a plain object
    if (typeof winCert === 'object' && winCert !== null && 'value' in winCert) {
      const v = winCert.value
      if (isCertUrl(v)) return v
    }
  }

  // 3. Check window.xxTrustedFormToken
  if (isCertUrl(window.xxTrustedFormToken)) return window.xxTrustedFormToken

  // 4. Check window.xxTrustedFormPingUrl (some versions use this)
  const winPing = window.xxTrustedFormPingUrl
  if (winPing) {
    if (isCertUrl(winPing)) return winPing
    if (winPing instanceof HTMLInputElement && isCertUrl(winPing.value))
      return winPing.value
  }

  // 5. Scan ALL hidden inputs for any cert URL
  const allHidden = document.querySelectorAll('input[type="hidden"]')
  for (let i = 0; i < allHidden.length; i++) {
    const el = allHidden[i] as HTMLInputElement
    if (isCertUrl(el.value)) return el.value
  }

  // 6. Check for TrustedForm iframe (it creates one) and look for data attributes
  const tfIframe = document.querySelector(
    'iframe[src*="trustedform"]',
  ) as HTMLIFrameElement | null
  if (tfIframe) {
    const certFromAttr = tfIframe.getAttribute('data-tf-cert-url')
    if (isCertUrl(certFromAttr)) return certFromAttr!
  }

  return ''
}

export function useLeadTracking() {
  const [leadIdToken, setLeadIdToken] = useState('')
  const [trustedFormUrl, setTrustedFormUrl] = useState('')
  const initialized = useRef(false)
  const found = useRef(false)

  const onCertFound = useCallback((url: string) => {
    if (found.current) return
    found.current = true
    console.log('[LeadTracking] ✅ TrustedForm cert URL captured:', url)
    setTrustedFormUrl(url)
  }, [])

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    // --- Find a form element to attach hidden inputs to ---
    const formEl = document.querySelector('form') || document.body

    // --- Create hidden input for LeadID ---
    if (!document.querySelector('input[name="leadid_token"]')) {
      const leadInput = document.createElement('input')
      leadInput.type = 'hidden'
      leadInput.name = 'leadid_token'
      leadInput.id = 'leadid_token'
      leadInput.value = ''
      formEl.appendChild(leadInput)
    }

    // --- Create hidden input for TrustedForm if not already in DOM ---
    if (!document.querySelector('input[name="xxTrustedFormCertUrl"]')) {
      const tfInput = document.createElement('input')
      tfInput.type = 'hidden'
      tfInput.name = 'xxTrustedFormCertUrl'
      tfInput.id = 'xxTrustedFormCertUrl'
      tfInput.value = ''
      formEl.appendChild(tfInput)
      console.log(
        '[LeadTracking] Created hidden input for TrustedForm inside',
        formEl.tagName,
      )
    } else {
      console.log(
        '[LeadTracking] TrustedForm hidden input already exists in DOM',
      )
    }

    // --- LeadID Script ---
    window.myCallbackFunction = (token: string) => {
      console.log('[LeadTracking] LeadID token captured:', token)
      setLeadIdToken(token)
      const field = document.getElementById(
        'leadid_token',
      ) as HTMLInputElement | null
      if (field) field.value = token
    }

    if (!document.getElementById('LeadiDscript_campaign')) {
      const leadIdScript = document.createElement('script')
      leadIdScript.id = 'LeadiDscript_campaign'
      leadIdScript.type = 'text/javascript'
      leadIdScript.async = true
      leadIdScript.src =
        '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction'
      document.body.appendChild(leadIdScript)
    }

    // --- TrustedForm Script ---
    function loadTrustedForm() {
      console.log('[LeadTracking] Loading TrustedForm script...')
      console.log(
        '[LeadTracking] Form element found:',
        !!document.querySelector('form'),
      )
      const field = 'xxTrustedFormCertUrl'
      const provideReferrer = false
      const tf = document.createElement('script')
      tf.type = 'text/javascript'
      tf.async = true
      tf.src =
        'http' +
        (document.location.protocol === 'https:' ? 's' : '') +
        '://api.trustedform.com/trustedform.js?provide_referrer=' +
        escape(String(provideReferrer)) +
        '&field=' +
        escape(field) +
        '&l=' +
        new Date().getTime() +
        Math.random()

      tf.onload = () => {
        console.log('[LeadTracking] TrustedForm script loaded successfully')
        // After script loads, do an immediate scan
        setTimeout(() => {
          debugTrustedFormState()
          const url = scanForTrustedFormUrl()
          if (url) onCertFound(url)
        }, 1000)
      }

      tf.onerror = (err) => {
        console.error(
          '[LeadTracking] ❌ TrustedForm script FAILED to load:',
          err,
        )
      }

      const s = document.getElementsByTagName('script')[0]
      if (s && s.parentNode) {
        s.parentNode.insertBefore(tf, s)
      } else {
        document.head.appendChild(tf)
      }
    }

    function debugTrustedFormState() {
      console.log('[LeadTracking] --- TrustedForm Debug ---')

      // Check window properties
      console.log(
        '[LeadTracking] window.xxTrustedFormCertUrl:',
        window.xxTrustedFormCertUrl,
      )
      console.log(
        '[LeadTracking] window.xxTrustedFormToken:',
        window.xxTrustedFormToken,
      )
      console.log(
        '[LeadTracking] window.xxTrustedFormPingUrl:',
        window.xxTrustedFormPingUrl,
      )

      // Check hidden inputs
      const inputs = document.querySelectorAll(
        'input[name="xxTrustedFormCertUrl"]',
      )
      console.log(
        '[LeadTracking] Inputs with name="xxTrustedFormCertUrl":',
        inputs.length,
      )
      inputs.forEach((inp, i) => {
        const el = inp as HTMLInputElement
        console.log(
          `[LeadTracking]   Input ${i}: value="${el.value}", id="${el.id}", type="${el.type}"`,
        )
      })

      // Check for TrustedForm iframe
      const iframes = document.querySelectorAll('iframe')
      iframes.forEach((iframe, i) => {
        if (iframe.src && iframe.src.indexOf('trustedform') !== -1) {
          console.log(
            `[LeadTracking] TrustedForm iframe found: src="${iframe.src}"`,
          )
        }
      })

      // Check all window properties containing 'trust' or 'cert'
      const tfKeys = Object.keys(window).filter(
        (k) =>
          k.toLowerCase().indexOf('trust') !== -1 ||
          k.toLowerCase().indexOf('cert') !== -1,
      )
      if (tfKeys.length > 0) {
        console.log('[LeadTracking] Window properties with trust/cert:', tfKeys)
        tfKeys.forEach((k) => {
          const val = (window as any)[k]
          console.log(
            `[LeadTracking]   window.${k}:`,
            typeof val === 'object' ? val : val,
          )
        })
      }

      console.log('[LeadTracking] --- End Debug ---')
    }

    // Load TrustedForm immediately (don't wait for window.load in SPA)
    loadTrustedForm()

    // --- Aggressive polling fallback ---
    let pollCount = 0
    const pollInterval = setInterval(() => {
      if (found.current) {
        clearInterval(pollInterval)
        return
      }

      const url = scanForTrustedFormUrl()
      if (url) {
        onCertFound(url)
        clearInterval(pollInterval)
        return
      }

      pollCount++
      // Log debug state every 5 seconds
      if (pollCount % 10 === 0) {
        console.log(
          `[LeadTracking] Still polling for TrustedForm cert (${pollCount * 500}ms elapsed)...`,
        )
        debugTrustedFormState()
      }
    }, 500)

    const timeout = setTimeout(() => {
      clearInterval(pollInterval)
      if (!found.current) {
        console.warn(
          '[LeadTracking] ⚠️ TrustedForm cert URL not found after 60s',
        )
        debugTrustedFormState()
      }
    }, 60000)

    return () => {
      clearInterval(pollInterval)
      clearTimeout(timeout)
    }
  }, [onCertFound])

  return { leadIdToken, trustedFormUrl }
}
